import { useEffect, useMemo, useState } from 'react';
import { App, Empty, Pagination, Skeleton, Button } from 'antd';
import { Plus } from 'lucide-react';
import { create } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { channelApi } from '@/api/channel.api';
import { ApiError } from '@/api/errors';
import { useAgentNameMap } from '@/hooks/useAgentNameMap';
import { ListChannelAccountsRequestSchema } from '@/proto/agentplatform/httpv2/channel_pb';
import {
  AccountStatus,
  ChannelType,
  type ChannelAccount,
} from '@/proto/agentplatform/v1/channel_pb';
import ChannelTypeSidebar from './ChannelAccountComponents/ChannelTypeSidebar';
import ChannelAccountToolbar, {
  type StatusFilter,
  type ViewMode,
} from './ChannelAccountComponents/ChannelAccountToolbar';
import ChannelAccountTable from './ChannelAccountComponents/ChannelAccountTable';
import ChannelAccountCard from './ChannelAccountComponents/ChannelAccountCard';
import ChannelAccountFormDrawer, {
  type AccountDrawerMode,
  type RevealedToken,
} from './ChannelAccountComponents/ChannelAccountFormDrawer';
import MultiTokenRevealDialog from './ChannelAccountComponents/MultiTokenRevealDialog';
import FeishuRegisterModal from './ChannelAccountComponents/FeishuRegisterModal';
import { CHANNEL_TYPES } from './ChannelAccountComponents/helpers';
import './ChannelAccountComponents/channel-accounts.css';

const VIEW_STORAGE_KEY = 'platform-cha-view';

const TYPE_URL_PARAM = 'type';
const TYPE_TO_SLUG: Record<number, string> = {
  [ChannelType.BRIDGE]: 'bridge',
  [ChannelType.FEISHU]: 'feishu',
  [ChannelType.QQ]: 'qq',
};
const SLUG_TO_TYPE: Record<string, ChannelType> = {
  bridge: ChannelType.BRIDGE,
  feishu: ChannelType.FEISHU,
  qq: ChannelType.QQ,
};

export default function ChannelAccountsPage() {
  const { modal, message } = App.useApp();
  const queryClient = useQueryClient();

  // activeType 通过 URL 参数持久化，进入详情后浏览器返回可回到相同 tab。
  const [searchParams, setSearchParams] = useSearchParams();
  const activeType = SLUG_TO_TYPE[searchParams.get(TYPE_URL_PARAM) ?? ''] ?? ChannelType.BRIDGE;
  const setActiveType = (t: ChannelType) => {
    const next = new URLSearchParams(searchParams);
    next.set(TYPE_URL_PARAM, TYPE_TO_SLUG[t] ?? 'bridge');
    setSearchParams(next, { replace: true });
  };
  // 当前 tab 的 URL slug：进入详情时带入 ?type=<slug>，返回时据此落回原 tab。
  const activeTypeSlug = TYPE_TO_SLUG[activeType];
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode) || 'table'
  );
  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [boundAgentFilter, setBoundAgentFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<AccountDrawerMode>('create');
  const [editAccountId, setEditAccountId] = useState<string | null>(null);
  const [revealedTokens, setRevealedTokens] = useState<RevealedToken[]>([]);
  const [feishuRegisterOpen, setFeishuRegisterOpen] = useState(false);

  // 切换类型时重置分页与筛选，避免跨类型串页。
  useEffect(() => {
    setPage(1);
    setStatusFilter('all');
    setBoundAgentFilter(undefined);
  }, [activeType]);

  const listParams = useMemo(() => {
    const kw = searchKeyword.trim();
    return create(ListChannelAccountsRequestSchema, {
      page,
      pageSize,
      type: activeType,
      nameOrId: kw || undefined,
      agentId: boundAgentFilter || undefined,
    });
  }, [activeType, page, pageSize, searchKeyword, boundAgentFilter]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'channel_accounts',
      activeType,
      page,
      pageSize,
      searchKeyword,
      boundAgentFilter,
    ] as const,
    queryFn: () => channelApi.listChannelAccounts(listParams),
  });

  const rows = useMemo(() => (data?.items ?? []).filter(Boolean), [data?.items]);
  const totalCount = data?.total ?? 0;

  // 状态筛选参考页为客户端过滤（当前页数据），保留该语义。
  // 与列表状态标签对齐：ONLINE → 已启用，其余 → 已停用。
  const visibleRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((a) =>
      statusFilter === 'enabled'
        ? a.status === AccountStatus.ONLINE
        : a.status !== AccountStatus.ONLINE
    );
  }, [rows, statusFilter]);

  // 侧栏计数：三种类型都走真实接口（pageSize 1 只取 total）。
  const websocketCount = useTypeCount(ChannelType.BRIDGE);
  const feishuCount = useTypeCount(ChannelType.FEISHU);
  const qqCount = useTypeCount(ChannelType.QQ);
  const countsByType: Record<number, number> = {
    [ChannelType.BRIDGE]: websocketCount,
    [ChannelType.FEISHU]: feishuCount,
    [ChannelType.QQ]: qqCount,
  };

  // 仅刷新当前类型对应的列表 + 侧栏计数，避免全量 invalidate 触发其他类型无谓请求。
  function invalidateActiveType() {
    queryClient.invalidateQueries({ queryKey: ['channel_accounts', activeType] });
    queryClient.invalidateQueries({ queryKey: ['channel_accounts', 'count', activeType] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelApi.deleteChannelAccount(id),
    onSuccess: () => {
      invalidateActiveType();
      message.success('已删除');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.reason === 'account_enabled') {
        message.warning('该渠道账号已启用，请先停用后再删除');
        return;
      }
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    },
  });

  const enableMutation = useMutation({
    mutationFn: (id: string) => channelApi.enableChannelAccount(id),
    onSuccess: () => {
      invalidateActiveType();
      message.success('已启用');
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const disableMutation = useMutation({
    mutationFn: (id: string) => channelApi.disableChannelAccount(id),
    onSuccess: () => {
      invalidateActiveType();
      // BRIDGE 账号停用会级联停用其 WEBSOCKET token，刷新 token 相关视图。
      queryClient.invalidateQueries({ queryKey: ['agent-tokens'] });
      message.success('已停用');
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const toggling = enableMutation.isPending || disableMutation.isPending;

  function handleToggleDisable(item: ChannelAccount) {
    if (item.disabled) {
      enableMutation.mutate(item.id);
      return;
    }
    modal.confirm({
      title: '停用渠道账号',
      content: `停用「${item.name || item.id}」后将断开连接并通知 Worker 停止。`,
      okText: '停用',
      okType: 'danger',
      onOk: () => disableMutation.mutateAsync(item.id),
    });
  }

  function handleDelete(item: ChannelAccount) {
    modal.confirm({
      title: '确认删除',
      content: `将删除渠道账号「${item.name || item.id}」并通知 Worker 停止，不可撤销。`,
      okText: '删除',
      okType: 'danger',
      onOk: () => deleteMutation.mutateAsync(item.id),
    });
  }

  function handleCreate() {
    setDrawerMode('create');
    setEditAccountId(null);
    setDrawerOpen(true);
  }

  function handleEdit(item: ChannelAccount) {
    setDrawerMode('edit');
    setEditAccountId(item.id);
    setDrawerOpen(true);
  }

  const activeLabel = CHANNEL_TYPES.find((t) => t.type === activeType)?.label ?? '';

  // 「绑定 Agent」筛选下拉：复用全站共享的 agentId → name 映射，展示全部 Agent（可搜索）。
  // 各渠道类型（BRIDGE / 飞书 / QQ）账号都带 agentId，故所有 tab 都展示该筛选。
  const agentNameMap = useAgentNameMap();
  const boundAgentOptions = useMemo(
    () =>
      Object.entries(agentNameMap).map(([id, name]) => ({
        value: id,
        label: `${name}（${id}）`,
      })),
    [agentNameMap]
  );

  return (
    <div className="pp-page cha-page">
      <header className="pp-head">
        <div>
          <h2>渠道账号</h2>
          <p>管理 WebSocket / 飞书 / QQ 等渠道账号 · Agent 通过渠道账号对外服务</p>
        </div>
      </header>

      <div className="cha-shell">
        <ChannelTypeSidebar
          activeType={activeType}
          onChange={setActiveType}
          countsByType={countsByType}
        />

        <main className="acc-main">
          <ChannelAccountToolbar
            activeLabel={activeLabel}
            count={visibleRows.length}
            search={searchKeyword}
            onSearchChange={(v) => {
              setSearchKeyword(v);
              setPage(1);
            }}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onCreate={handleCreate}
            onFeishuQuickCreate={
              activeType === ChannelType.FEISHU ? () => setFeishuRegisterOpen(true) : undefined
            }
            showBoundAgentFilter
            boundAgentFilter={boundAgentFilter}
            onBoundAgentFilterChange={setBoundAgentFilter}
            boundAgentOptions={boundAgentOptions}
          />

          {isLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : viewMode === 'table' ? (
            visibleRows.length === 0 ? (
              <Empty description={`还没有 ${activeLabel} 渠道账号`}>
                <Button type="primary" icon={<Plus size={14} />} onClick={handleCreate}>
                  新建第一个
                </Button>
              </Empty>
            ) : (
              <ChannelAccountTable
                data={visibleRows}
                typeSlug={activeTypeSlug}
                onToggleDisable={handleToggleDisable}
                onEdit={handleEdit}
                onDelete={handleDelete}
                toggling={toggling}
              />
            )
          ) : (
            <div className="acc-grid">
              {visibleRows.map((item) => (
                <ChannelAccountCard
                  key={item.id}
                  item={item}
                  typeSlug={activeTypeSlug}
                  onToggleDisable={handleToggleDisable}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  toggling={toggling}
                />
              ))}
            </div>
          )}

          {!isLoading && totalCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={totalCount}
                showSizeChanger
                pageSizeOptions={[10, 20, 50, 100]}
                showTotal={(t) => `共 ${t} 条`}
                onChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
              />
            </div>
          )}
        </main>
      </div>

      <ChannelAccountFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        editAccountId={editAccountId}
        defaultType={activeType}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerMode('create');
          setEditAccountId(null);
        }}
        onSaved={() => {
          setDrawerOpen(false);
          setDrawerMode('create');
          setEditAccountId(null);
        }}
        onTokensRevealed={setRevealedTokens}
      />

      <MultiTokenRevealDialog
        open={revealedTokens.length > 0}
        tokens={revealedTokens}
        onClose={() => setRevealedTokens([])}
      />

      <FeishuRegisterModal
        open={feishuRegisterOpen}
        onClose={() => setFeishuRegisterOpen(false)}
        onSuccess={() => {
          setFeishuRegisterOpen(false);
          queryClient.invalidateQueries({ queryKey: ['channel_accounts', ChannelType.FEISHU] });
          queryClient.invalidateQueries({
            queryKey: ['channel_accounts', 'count', ChannelType.FEISHU],
          });
        }}
      />
    </div>
  );
}

/** 侧栏计数：仅拿 total，pageSize 1 避免多拉数据。 */
function useTypeCount(type: ChannelType): number {
  const { data } = useQuery({
    queryKey: ['channel_accounts', 'count', type] as const,
    queryFn: () =>
      channelApi.listChannelAccounts(
        create(ListChannelAccountsRequestSchema, {
          page: 1,
          pageSize: 1,
          type,
        })
      ),
  });
  return data?.total ?? 0;
}
